import { isValidEmail, isValidPhoneNumber } from '@blackbird/common/data-validator';
import { Add as AddIcon } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import { Button, Checkbox, FormControlLabel, Grid, IconButton, MenuItem, TextField } from '@mui/material';
import { Theme } from '@mui/material/styles';
import * as Schema from 'generated/graphql/schema';
import { cloneDeep } from 'lodash';
import * as React from 'react';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';

import WrapRequiredFieldsDescriptionComponent from '@/views/common-components/wrap-required-fileds-description';
import { UpdateConfigInput } from '@/views/dialogs/sensor-settings/sensor-settings-dialog';

import { TabPropsType } from '.';

const useStyles = makeStyles()((theme: Theme) => ({
  tabPanel: {
    padding: '20px',
    minHeight: '350px',
    width: '100%',
    [theme.breakpoints.down('md')]: {
      width: '70%',
    },
    [theme.breakpoints.down('sm')]: {
      width: '60%',
    },
  },
  textFields: {
    width: '100%',
  },
  checkboxField: {
    marginLeft: '0px',
  },
  topMargin: {
    marginTop: '16px',
  },
}));

const subscriberTypesObj = Schema.SubscriberType;

export const TabProps: TabPropsType = {
  isValid: (state) => state.name.length > 0 && !!state?.config?.sensor?.type,
  label: (t) => t('device:missingDataAlarms', { defaultValue: 'Missing data alarms' }),
};

type Properties = {
  handleChange: (newConfig: Partial<UpdateConfigInput>, meta?: { name?: string; description?: string }) => void;
  stateConfigInput?:
    | (Omit<Schema.SensorConfigInput, 'wiring'> & {
        wiring?: Omit<Schema.SensorWiringState, '__typename'> | null;
      })
    | null;
  invalidFormMessage: React.ReactNode | null;
};

export const TabPanel: FC<Properties> = (props) => {
  const { handleChange, stateConfigInput, invalidFormMessage } = props;
  const [t] = useTranslation();
  const { classes } = useStyles();

  const alarmConfigData = { ...(stateConfigInput?.dataAlarm ?? ({} as Schema.DataAlarmInput)) };

  const addItem = () => {
    const subscribers = alarmConfigData.subscribers ? alarmConfigData.subscribers : [];
    const newSubscriber = { type: subscriberTypesObj.EMAIL, value: '', languageCode: 'en' };

    handleChange({
      sensor: {
        dataAlarm: {
          ...alarmConfigData,
          subscribers: [...subscribers, newSubscriber],
        },
      },
    });
  };

  const removeItem = (index: number) => () => {
    const { subscribers = [] }: { subscribers?: Schema.SubscriberInput[] } = { ...alarmConfigData };
    const newSubscribers = subscribers.filter((item, i) => {
      if (i !== index) {
        return item;
      }
    });

    handleChange({
      sensor: {
        dataAlarm: {
          ...alarmConfigData,
          subscribers: newSubscribers,
        },
      },
    });
  };

  const onDataAlarmChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    let dataAlarm = { ...(alarmConfigData || {}) } as Schema.DataAlarmInput;

    const name = e.target.name;
    const value = e.target.value;

    if (dataAlarm && name === 'updateBuffer') {
      dataAlarm.updateBuffer = parseInt(value, 10).seconds;
    } else if (dataAlarm && name === 'notificationFrequency') {
      dataAlarm.notificationFrequency = parseInt(value, 10).seconds;
    } else if (dataAlarm && name === 'repeatNotification' && 'checked' in e.target) {
      dataAlarm.repeatNotification = e.target.checked;
    }

    handleChange({
      sensor: {
        dataAlarm,
      },
    });
  };

  const onDataAlarmSubscriberChange =
    (index: number) => (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      let updatedSubscribers = cloneDeep(alarmConfigData.subscribers);

      const name = event.target.name;
      const value = event.target.value;

      if (name === 'type') {
        updatedSubscribers[index].type = value as Schema.SubscriberType;
      } else if (name === 'value') {
        updatedSubscribers[index].value = value;
      }

      handleChange({
        sensor: {
          dataAlarm: {
            ...alarmConfigData,
            subscribers: updatedSubscribers,
          },
        },
      });
    };

  const alarmSubscriptionValid = (type: Schema.SubscriberType, value: string) => {
    switch (type) {
      case Schema.SubscriberType.EMAIL:
        return isValidEmail(value);
      case Schema.SubscriberType.SMS:
        return isValidPhoneNumber(value);
      default:
        console.error('Unrecognized subscriber type');
        return false;
    }
  };

  const SubscriberInput = (subscriber: Schema.SubscriberInput, index: number) => {
    return (
      <Grid spacing={1} alignItems="center" container>
        <Grid item xs={5}>
          <TextField
            select
            onChange={onDataAlarmSubscriberChange(index)}
            variant="filled"
            className={classes.textFields}
            name="type"
            label={t(['device:subscriptionType'], { defaultValue: 'Subscription type' })}
            value={subscriber.type || Schema.SubscriberType.EMAIL}
            helperText={' '}
            required
          >
            {AlarmTypes()}
          </TextField>
        </Grid>

        <Grid item xs={5}>
          <TextField
            className={classes.textFields}
            variant="filled"
            name="value"
            type="text"
            label={t(['device:subscriptionValue'], { defaultValue: 'Email/Phone No' })}
            required
            value={subscriber.value ? subscriber.value : ''}
            onChange={onDataAlarmSubscriberChange(index)}
            error={!alarmSubscriptionValid(subscriber.type, subscriber.value)}
            helperText={
              !alarmSubscriptionValid(subscriber.type, subscriber.value)
                ? subscriberTypeToErrorMeesage(subscriber.type)
                : ' '
            }
          />
        </Grid>
        <Grid item xs={2}>
          <IconButton onClick={removeItem(index)} size="large">
            <CloseIcon />
          </IconButton>
        </Grid>
      </Grid>
    );
  };

  const subscriberTypeToErrorMeesage = (subType: Schema.SubscriberType) => {
    switch (subType) {
      case Schema.SubscriberType.EMAIL:
        return t(['shared:emailNotValid'], { defaultValue: 'Not a valid e-mail' });
      case Schema.SubscriberType.SMS:
        return t(['shared:phoneNotValid'], {
          defaultValue: 'Not a valid phone number. Note: must contain country code!',
        });
      default:
        console.error('Unrecognized SubscriptionType! ', subType);
        return 'Error';
    }
  };
  const AlarmTypes = () => {
    const menuItems = [];
    for (const key in subscriberTypesObj) {
      if (subscriberTypesObj.hasOwnProperty(key)) {
        menuItems.push(
          <MenuItem key={key} value={key}>
            {key}
          </MenuItem>,
        );
      }
    }
    return menuItems;
  };

  const Subscribers =
    alarmConfigData &&
    alarmConfigData.subscribers &&
    alarmConfigData.subscribers.length > 0 &&
    alarmConfigData.subscribers.map((subscriber, index) => {
      return SubscriberInput(subscriber, index);
    });

  const updateBufferHasValue =
    !!stateConfigInput && !!stateConfigInput.dataAlarm && !!stateConfigInput.dataAlarm.updateBuffer;
  const updateBufferValue = updateBufferHasValue ? stateConfigInput!.dataAlarm!.updateBuffer! / 1000 : '';
  const updateBufferHasError = updateBufferHasValue && +updateBufferValue < 0;

  const notificationFrequencyHasValue = !!stateConfigInput && !!stateConfigInput.dataAlarm?.notificationFrequency;
  const notificationFrequencyValue = notificationFrequencyHasValue
    ? stateConfigInput!.dataAlarm!.notificationFrequency! / 1000
    : '';
  const notificationFrequencyHasError = notificationFrequencyHasValue && +notificationFrequencyValue < 0;

  return (
    <div className={classes.tabPanel}>
      <TextField
        className={classes.textFields}
        fullWidth
        variant="filled"
        name="updateBuffer"
        type="number"
        label={
          updateBufferHasError
            ? t(['shared:mustBePositive'], { defaultValue: 'Must be a positive number' })
            : t(['device:offlineDurationSeconds'], { defaultValue: 'Offline duration (seconds)' })
        }
        helperText={t(['device:offlineDurationHelperText'], {
          defaultValue: 'The time in seconds the sensor should be offline before you want to receive an alarm',
        })}
        value={updateBufferValue}
        error={updateBufferHasError}
        onChange={onDataAlarmChange}
      />

      <TextField
        className={`${classes.textFields} `}
        variant="filled"
        fullWidth
        name="notificationFrequency"
        type="number"
        label={t(['alarms:alarmThreshold'], { defaultValue: 'Notification threshold (seconds)' })}
        helperText={
          notificationFrequencyHasError
            ? t(['shared:mustBePositive'], { defaultValue: 'Must be a positive number' })
            : t(['alarms:alarmThresholdDescription'], {
                defaultValue: 'Minimum amount of time before a new alarm notification can be sent',
              })
        }
        value={notificationFrequencyValue}
        error={notificationFrequencyHasError}
        onChange={onDataAlarmChange}
      />
      <FormControlLabel
        className={classes.checkboxField}
        name="repeatNotification"
        control={
          <Checkbox
            checked={!!stateConfigInput?.dataAlarm?.repeatNotification}
            onChange={onDataAlarmChange}
            value={stateConfigInput?.dataAlarm?.repeatNotification}
            color="primary"
          />
        }
        label={`${t(['alarms:alarmRepeatDescription'], {
          defaultValue: 'Repeat notification while alarm is ongoing (limited by the notification threshold)',
        })}`}
      />
      <div className={classes.topMargin}>{Subscribers}</div>
      <Grid container>
        <Grid item xs={4}>
          <div onClick={addItem}>
            <Button color="primary">
              <AddIcon />
              {t(['alarms:addAlarmSubscriber'], { defaultValue: 'Add subscriber to alarm' })}
            </Button>
          </div>
        </Grid>
        <Grid item xs={8} />
      </Grid>

      {invalidFormMessage}
      <br />
      <WrapRequiredFieldsDescriptionComponent />
    </div>
  );
};
